import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';

/**
 * Profile selection helpers for the calendar's quick-create paths (PROD-983).
 *
 * The slot-geometry half of this module (`quickCreateStartISO`,
 * `dragDeltaToNewStartISO`, `addMinutesISO`) graduated into Signal DS with the
 * PACalendar* family, which is its only caller; import those from the package.
 */

/**
 * Preferred-or-first appointment profile from a provider's profile list —
 * mirrors Flutter `doc.appointmentProfile.where(isPreferred).first ?? .first`
 * (`calendar_tab.dart:541-550`).
 *
 * IMPORTANT (PR #427 review §2): this MUST run on the provider's RAW,
 * original-order `appointmentProfile` list — NOT the location-grouped
 * `quickBookGroups`, which `groupProfilesByLocation` re-sorts alphabetically
 * by `appointmentType.display` and regroups. Selecting from the regrouped
 * order would pick a different "first" than Flutter when no profile is
 * preferred (or break the tiebreak when two are). `find` already takes the
 * first match in iteration order, matching Dart's `.where(...).first`.
 * Returns null when the provider has no profiles.
 */
export function preferredProfileFromList(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): CreateAppointmentProfileRequest | null {
  if (!profiles || profiles.length === 0) return null;
  // Room-mitigation (2026-06-03): a provider can carry DUPLICATE profiles of
  // the same name where one has a usable room and the other has none. An
  // appointment booked with the room-less profile is un-openable +
  // un-reschedulable — the backend's `GetRoomByIdAsync` 500s/empties on a
  // missing/empty roomId (reproduces in Flutter too). So PREFER profiles that
  // carry a usable room; only fall back to the room-less set if NONE have a
  // room (so providers whose profiles are all room-less still get a default).
  // Within the chosen pool keep Flutter's `isPreferred ?? first` rule.
  const withRoom = profiles.filter((p) => hasRoomId(p.roomId));
  const pool = withRoom.length > 0 ? withRoom : profiles;
  // `isPreferred ?? first`: when no profile in the pool is preferred (all
  // `isPreferred: false`) this falls through to `pool[0]` — the deliberate
  // default. (review §5)
  return pool.find((p) => p.isPreferred === true) ?? pool[0];
}

/**
 * True when a roomId is merely PRESENT — a non-empty, non-all-zero GUID.
 *
 * NAME IS DELIBERATELY MODEST (review §4): this does NOT validate that the
 * room actually resolves. A profile can carry a non-empty but PHANTOM roomId
 * (a room id that no longer exists) and this still returns true — phantom
 * rooms are indistinguishable client-side (no field flags them; there is no
 * rooms-list endpoint to check against). It only catches the *missing* /
 * empty / all-zero cases. The phantom-duplicate case is handled best-effort by
 * `dedupeProfilesPreferRoom`; the robust fix is backend/data (room-fix ticket).
 */
export function hasRoomId(roomId: string | null | undefined): boolean {
  if (!roomId) return false;
  const t = roomId.trim();
  if (t === '' || t === '00000000-0000-0000-0000-000000000000') return false;
  return true;
}

/**
 * Defense-in-depth for the duplicate-profile / phantom-room bug (2026-06-03).
 *
 * A provider can carry DUPLICATE appointment profiles of the same
 * name+location — e.g. "First Appointment - Virtual" appears 3× for one
 * provider, where the live one points at a real room and the dead twins point
 * at a phantom room (`f43671d3`, which `GetRoomByIdAsync` can't resolve →
 * the appointment becomes un-openable/un-reschedulable; reproduces in Flutter
 * too). The dead twins are NOT distinguishable by any client field (same
 * non-empty roomId shape, all `isDefault`, no `isArchived`) and there is no
 * rooms-list endpoint to validate against. So the best the client can do is
 * collapse same-name/same-location duplicates to ONE entry, preferring a
 * room-bearing twin and otherwise keeping the FIRST (the backend orders the
 * canonical/current profile first in practice — order-dependent, hence
 * "best-effort"). `isPreferred` is inherited from any collapsed twin so the
 * downstream `isPreferred ?? first` selection doesn't resurrect the dead one.
 *
 * THE ROBUST FIX IS BACKEND/DATA (remove the dead duplicates / repoint the
 * phantom room / null-guard `GetRoomByIdAsync`). This only reduces how often
 * React books the broken twin.
 */
export function dedupeProfilesPreferRoom(
  profiles: CreateAppointmentProfileRequest[] | undefined,
): CreateAppointmentProfileRequest[] {
  if (!profiles || profiles.length === 0) return [];
  const byKey = new Map<string, CreateAppointmentProfileRequest>();
  for (const p of profiles) {
    const locId = (p.location as { id?: string } | undefined)?.id ?? '';
    const key = `${p.profileName ?? ''}|${locId}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, p);
      continue;
    }
    // Inherit isPreferred from a collapsed twin so the kept (live) profile
    // wins the downstream `isPreferred ?? first` selection rather than the
    // dead-but-preferred twin being re-selected from the raw list elsewhere.
    // (If ALL twins are `isPreferred: false`, this stays false — downstream
    // falls through to first, which is correct. review §5)
    const inheritedPreferred = existing.isPreferred || p.isPreferred;
    // ⚠️ ORDER-DEPENDENT (review §2): when the kept twin already has a roomId,
    // we DON'T replace it — we keep whichever came FIRST. Both a live room and
    // a phantom room satisfy `hasRoomId` (phantoms are indistinguishable), so
    // correctness here RELIES ON the backend returning the canonical/live
    // profile first. If a dead twin ever sorts first, this keeps the dead one
    // (see the "dead twin sorts first" known-limitation test). Replacement only
    // upgrades a room-LESS kept entry to a room-bearing incoming one.
    const replace = !hasRoomId(existing.roomId) && hasRoomId(p.roomId);
    const kept = replace ? p : existing;
    byKey.set(key, { ...kept, isPreferred: inheritedPreferred });
  }
  return Array.from(byKey.values());
}
